import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AdminPageTitle, AdminSearch, AdminTabs, ConfirmationModal, ContentPreview, Drawer, FilterBar, FilterSelect, Panel, StatusBadge, UserCell } from '../../components/admin/AdminPrimitives';
import { DataTable, type Column } from '../../components/admin/DataTable';
import { Button } from '../../components/ui/Button';
import { adminUsers, moderationQueue, recordAudit, type ContentKind, type ModerationItem } from '../../data/admin';
import { useLocale } from '../../contexts/LocaleContext';
const kindLabels: Record<ContentKind, string> = {
  post: 'Posts',
  pulse: 'Pulses',
  comment: 'Comentários'
};
const statusLabels: Record<string, string> = {
  pending: 'Pendente',
  reviewing: 'Em análise',
  approved: 'Aprovado',
  hidden: 'Oculto',
  removed: 'Removido',
  escalated: 'Escalado'
};
type PendingAction = {
  item: ModerationItem;
  action: string;
} | null;
export function AdminModeration() {
  const {
    kind: param
  } = useParams();
  const {
    tl
  } = useLocale();
  const navigate = useNavigate();
  const kind = (['post', 'pulse', 'comment'].includes(param ?? '') ? param : 'post') as ContentKind;
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('all');
  const [reported, setReported] = useState('all');
  const [open, setOpen] = useState<ModerationItem | null>(null);
  const [pending, setPending] = useState<PendingAction>(null);
  const [resolved, setResolved] = useState<Record<string, string>>({});
  const rows = useMemo(() => {
    const term = query.trim().toLowerCase();
    return moderationQueue[kind].map(item => resolved[item.id] ? {
      ...item,
      status: resolved[item.id] as ModerationItem['status']
    } : item).filter(item => {
      if (status !== 'all' && item.status !== status) return false;
      if (reported === 'reported' && item.reports === 0) return false;
      if (!term) return true;
      return item.excerpt.toLowerCase().includes(term);
    });
  }, [kind, query, status, reported, resolved]);
  const author = (id: string) => adminUsers.find(user => user.id === id) ?? adminUsers[0];
  const columns: Column<ModerationItem>[] = [{
    id: 'content',
    header: 'Conteúdo',
    width: '34%',
    cell: row => <ContentPreview excerpt={row.excerpt} meta={`${row.category} · ${row.published}`} kind={row.kind} data-id="element-1748" data-mp="19qvnbl74d66l" />
  }, {
    id: 'author',
    header: 'Autor',
    width: '18%',
    sortValue: row => author(row.authorId).name,
    cell: row => <UserCell user={author(row.authorId)} data-id="element-1749" data-mp="1ojxckpn7wbi8" />
  }, {
    id: 'reason',
    header: 'Motivo',
    width: '20%',
    cell: row => <span className="text-[12px] leading-snug text-muted" data-id="element-1750" data-mp="d2ft3bt2e8n5">
          {row.reason ? tl(row.reason) : 'Revisão de rotina'}
        </span>
  }, {
    id: 'reports',
    header: 'Denúncias',
    align: 'right',
    sortValue: row => row.reports,
    cell: row => <span className={`bee-num font-bold ${row.reports > 2 ? 'text-gold-deep' : 'text-deep'}`} data-id="element-1751" data-mp="4m6v7163tyi9">
          {row.reports}
        </span>
  }, {
    id: 'status',
    header: 'Status',
    sortValue: row => row.status,
    cell: row => <StatusBadge status={row.status} label={statusLabels[row.status] ?? row.status} data-id="element-1752" data-mp="3mzenh2bezala" />
  }];
  return <div className="space-y-4" data-id="element-1753" data-mp="3i1cunpo6rwwm">
      <AdminPageTitle title="Moderação" subtitle="Fila unificada de conteúdo. Toda decisão é registrada na auditoria com autor, horário e estado anterior." data-id="element-1754" data-mp="3i24s761cg4pj" />

      <AdminTabs tabs={(['post', 'pulse', 'comment'] as ContentKind[]).map(item => ({
      id: item,
      label: kindLabels[item],
      count: moderationQueue[item].filter(entry => entry.status === 'pending').length
    }))} active={kind} onChange={next => navigate(`/admin/moderacao/${next}`)} data-id="element-1755" data-mp="3rxpw59pz3f1x" />

      <FilterBar data-id="element-1756" data-mp="26ka8jjq6qhtr">
        <AdminSearch value={query} onChange={setQuery} placeholder="Buscar no conteúdo" data-id="element-1757" data-mp="26jsfsx2qelql" />
        <FilterSelect label="Status" value={status} onChange={setStatus} options={[{
        id: 'all',
        label: 'Todos'
      }, {
        id: 'pending',
        label: 'Pendentes'
      }, {
        id: 'reviewing',
        label: 'Em análise'
      }, {
        id: 'approved',
        label: 'Aprovados'
      }, {
        id: 'hidden',
        label: 'Ocultos'
      }, {
        id: 'escalated',
        label: 'Escalados'
      }]} data-id="element-1758" data-mp="2bjn36qnast6w" />
        <FilterSelect label="Origem" value={reported} onChange={setReported} options={[{
        id: 'all',
        label: 'Tudo'
      }, {
        id: 'reported',
        label: 'Com denúncia'
      }]} data-id="element-1759" data-mp="2uae8nh45zzg0" />
      </FilterBar>

      <DataTable rows={rows} columns={columns} rowKey={row => row.id} onRowClick={setOpen} selectable bulkActions={[{
      id: 'approve',
      label: 'Aprovar selecionados',
      onSelect: ids => setResolved(prev => ({
        ...prev,
        ...Object.fromEntries(ids.map(id => [id, 'approved']))
      }))
    }]} emptyTitle="Fila vazia" emptyBody="Nenhum item corresponde aos filtros aplicados." actions={[{
      id: 'review',
      label: 'Abrir revisão',
      onSelect: setOpen
    }, {
      id: 'approve',
      label: 'Aprovar',
      onSelect: row => setPending({
        item: row,
        action: 'approved'
      })
    }, {
      id: 'hide',
      label: 'Ocultar',
      onSelect: row => setPending({
        item: row,
        action: 'hidden'
      })
    }, {
      id: 'remove',
      label: 'Remover',
      destructive: true,
      onSelect: row => setPending({
        item: row,
        action: 'removed'
      })
    }, {
      id: 'warn',
      label: 'Advertir autor',
      onSelect: row => setPending({
        item: row,
        action: 'warned'
      })
    }, {
      id: 'escalate',
      label: 'Escalar',
      onSelect: row => setPending({
        item: row,
        action: 'escalated'
      })
    }]} data-id="element-1760" data-mp="14u53nvstl8mr" />

      <Panel title="Ações em massa" data-id="element-1761" data-mp="35r5bwoptpgk5">
        <p className="text-[12.5px] leading-relaxed text-muted" data-id="element-1762" data-mp="35pu5si8wnwpv">
          Apenas a aprovação está disponível em lote. Ocultar, remover, advertir e escalar
          exigem revisão individual, porque afetam a conta do autor e precisam de
          justificativa na auditoria.
        </p>
      </Panel>

      <Drawer open={open !== null} title="Revisão de conteúdo" subtitle={open ? `${kindLabels[open.kind]} · ${open.published}` : undefined} onClose={() => setOpen(null)} footer={<>
            <Button variant="outline" onClick={() => {
        if (open) setPending({
          item: open,
          action: 'hidden'
        });
      }} data-id="element-1764" data-mp="285kkxjb3l33m">
              Ocultar
            </Button>
            <Button variant="gold" onClick={() => {
        if (open) setPending({
          item: open,
          action: 'approved'
        });
      }} data-id="element-1765" data-mp="2h6sg3sibhnth">
              Aprovar
            </Button>
          </>} data-id="element-1763" data-mp="30slp6m3eo0qq">
        {open && <div className="space-y-3" data-id="element-1766" data-mp="2m5vamj0o8bn7">
            <Panel title="Conteúdo" data-id="element-1767" data-mp="2m4u1wl74o18n">
              <p className="text-[13.5px] leading-relaxed text-deep" data-id="element-1768" data-mp="2m3snc6qwkwm6">{open.excerpt}</p>
              <p className="mt-2 text-[11.5px] text-muted" data-id="element-1769" data-mp="2zz4hod15cl2k">
                {open.category} · publicado em {open.published}
              </p>
            </Panel>
            <Panel title="Autor" data-id="element-1770" data-mp="39w95siejyqm0">
              <UserCell user={author(open.authorId)} secondary={author(open.authorId).email} data-id="element-1771" data-mp="39t3hh5dqzsrc" />
            </Panel>
            <Panel title="Motivo e denúncias" data-id="element-1772" data-mp="34ua3lbujtzqn">
              <p className="text-[12.5px] leading-relaxed text-deep" data-id="element-1773" data-mp="g24pl17cxhsv">
                {open.reason ? tl(open.reason) : 'Revisão de rotina, sem denúncia associada.'}
              </p>
              <p className="bee-num mt-1 text-[12px] text-muted" data-id="element-1774" data-mp="p3cit4v0nsg3">
                {open.reports} denúncia(s) · status atual: {statusLabels[open.status]}
              </p>
            </Panel>
            <Panel title="Histórico" data-id="element-1775" data-mp="3sl369byaqive">
              <p className="text-[12.5px] leading-relaxed text-muted" data-id="element-1776" data-mp="3sm5vzgjvuf9q">
                Nenhuma decisão anterior neste item. A próxima ação será registrada com o seu
                nome, o estado anterior e o novo estado.
              </p>
            </Panel>
          </div>}
      </Drawer>

      <ConfirmationModal open={pending !== null} destructive={pending?.action === 'removed'} title={pending?.action === 'removed' ? 'Remover este conteúdo?' : pending?.action === 'warned' ? 'Advertir o autor?' : 'Confirmar decisão'} body={pending?.action === 'removed' ? 'O conteúdo sai do ar imediatamente e o autor é notificado. A ação é permanente e fica registrada na auditoria.' : 'A decisão passa a valer imediatamente e fica registrada na auditoria com o estado anterior e o novo.'} confirmLabel="Confirmar" requireText={pending?.action === 'removed' ? 'REMOVER' : undefined} onConfirm={() => {
      if (pending && pending.action !== 'warned') {
        setResolved(prev => ({
          ...prev,
          [pending.item.id]: pending.action
        }));
      }
      if (pending) {
        recordAudit({
          action: {
            'pt-BR': `Decisão de moderação: ${pending.action}`,
            'en-US': `Moderation decision: ${pending.action}`
          },
          entity: pending.item.id,
          context: {
            'pt-BR': 'Fila de moderação',
            'en-US': 'Moderation queue'
          },
          after: pending.action,
          severity: 'sensitive'
        });
      }
      setPending(null);
      setOpen(null);
    }} onClose={() => setPending(null)} data-id="element-1777" data-mp="b4uzdjc5923z" />
    </div>;
}